import { useIsMounted } from '@genie-fintech/ui/hooks'
import { container as innerContainer, grid } from './styles.css'
import { trueOrUndefined } from '@genie-fintech/ui/functions'
import { flexColumn, flexRow } from '$styles/common.css'
import { footnote, title } from '@genie-fintech/ui/style/typography'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { SERVICE_USER_TYPE, TAppUserDetail } from '$services/api'
import { FC, useState } from 'react'
import { getPhone } from '$app/utils'
import { format } from 'date-fns'
import { container } from '../styles.css'
import { status } from '@genie-fintech/ui/style/element'
import Table, { Column } from '$components/Table'
import { viewButton } from '$components/Table/styles.css'
// import AppUserAssignRoles from '../../AssignRoles'
import DeleteWithConfirm from '$components/DeleteWithConfirm'
import PreviewPermissionDrawer from '$blocks/PreviewPermissionDrawer'
import { useAssignRoleStore } from '$hooks/stores'
import { TRoles } from '$services/types'
import { Button } from '@genie-fintech/ui/components'
import { redirect, ROUTE_NAMES } from '$router/config'

const { colors } = themeVars

type Props = {
  handleOnDelete: (id: number, deleteRole: TRoles) => void
  user: TAppUserDetail
  appId?: string
  isLoading?: boolean
}

type ColumnData = TAppUserDetail['roles'][number]

const UserInfo: FC<Props> = ({ user, appId, handleOnDelete, isLoading }) => {
  const isMounted = useIsMounted()
  const userRoles = useAssignRoleStore(s => s.userRoles)

  const [selectedRole, setSelectedRole] = useState<{
    id: number
    name: string
  }>()

  const isInternalUser = user.user_type === SERVICE_USER_TYPE.INTERNAL

  const columns: Column<ColumnData>[] = [
    {
      name: 'No',
      render: ({ index }) => index + 1
    },
    {
      name: 'Role Name',
      dataIndex: 'name'
    },
    {
      name: 'Country',
      dataIndex: 'country',
      render: ({ record }) => {
        return (
          <span
            className={status({
              kind: 'tertiary',
              size: 'small',
              type: 'faint'
            })}
            style={{
              fontSize: '1rem', // hack to calculate for status font size
              textTransform: 'uppercase'
            }}
          >
            <span className={footnote.two}>
              {record.country.name.toLocaleUpperCase()}
            </span>
          </span>
        )
      }
    },
    {
      name: 'Permission',
      dataIndex: 'permission_count',
      render: ({ record }) => {
        return (
          <button
            className={viewButton}
            onClick={() =>
              setSelectedRole({
                id: record.id,
                name: record.name
              })
            }
          >
            View
          </button>
        )
      }
    },
    {
      name: 'Group',
      dataIndex: 'group',
      render: ({ record }) => record.group.name
    },
    {
      name: '',
      dataIndex: 'id',
      render: ({ record }) => {
        return (
          <DeleteWithConfirm
            loading={isLoading}
            styleVariants={{
              kind: 'neutral',
              category: 'icon',
              type: 'text'
            }}
            onConfirm={() => {
              handleOnDelete(record.id, record)
            }}
          />
        )
      }
    }
  ]

  return (
    <>
      <article className={container}>
        <article
          className={innerContainer}
          data-ready={trueOrUndefined(isMounted)}
        >
          <article className={flexColumn} style={{ gap: 4 }}>
            <p className={title.two}>USER INFO</p>
            <p className={footnote.one} style={{ color: colors.neutral[60] }}>
              View detailed information about each group to manage roles and
              permissions effectively.
            </p>
          </article>

          <article className={grid}>
            <article className={flexColumn} style={{ gap: 4 }}>
              <label
                className={footnote.two}
                style={{ color: colors.neutral[60] }}
              >
                User Name
              </label>
              <span
                className={title.three}
                style={{ color: colors.text.light }}
              >
                {user.name}
              </span>
            </article>

            <article className={flexColumn} style={{ gap: 4 }}>
              <label
                className={footnote.two}
                style={{ color: colors.neutral[60] }}
              >
                Date of Birth
              </label>
              <span
                className={title.three}
                style={{ color: colors.text.light }}
              >
                {user.date_of_birth
                  ? format(user.date_of_birth, 'dd MMM yyyy')
                  : '-'}
              </span>
            </article>

            <article className={flexColumn} style={{ gap: 4 }}>
              <label
                className={footnote.two}
                style={{ color: colors.neutral[60] }}
              >
                Gender
              </label>
              <span
                className={title.three}
                style={{
                  color: colors.text.light,
                  textTransform: 'capitalize'
                }}
              >
                {user.gender}
              </span>
            </article>

            <article className={flexColumn} style={{ gap: 4 }}>
              <label
                className={footnote.two}
                style={{ color: colors.neutral[60] }}
              >
                Email
              </label>
              <span
                className={title.three}
                style={{ color: colors.text.light }}
              >
                {user.email}
              </span>
            </article>

            <article className="flex flex-col gap-0.5">
              <label
                className={footnote.two}
                style={{ color: colors.neutral[60] }}
              >
                Phone Number
              </label>
              <span
                className={title.three}
                style={{ color: colors.text.light }}
              >
                {getPhone({
                  phoneCode: user.phone_code,
                  phoneNo: user.phone_no
                })}
              </span>
            </article>

            <article className={flexColumn} style={{ gap: 4 }}>
              <label
                className={footnote.two}
                style={{ color: colors.neutral[60] }}
              >
                Address
              </label>
              <span
                className={title.three}
                style={{ color: colors.text.light }}
              >
                {user.address ?? '-'}
              </span>
            </article>
          </article>
        </article>
      </article>

      <article className={container}>
        <article
          className={innerContainer}
          data-ready={trueOrUndefined(isMounted)}
        >
          <article
            className={flexRow}
            style={{ gap: 8, justifyContent: 'space-between' }}
          >
            <article className={flexColumn} style={{ gap: 4 }}>
              <p className={title.two}>LOGIN USER TYPE</p>
              <p className={footnote.one} style={{ color: colors.neutral[60] }}>
                The login behavior may vary depending on the selected login user
                type.
              </p>
            </article>

            <span
              className={status({
                kind: isInternalUser ? 'secondary' : 'primary',
                size: 'max',
                type: 'faint',
                inline: true
              })}
            >
              {isInternalUser ? 'Internal User' : 'External User'}
            </span>
          </article>
        </article>
      </article>

      <article className={container}>
        <article
          className={innerContainer}
          data-ready={trueOrUndefined(isMounted)}
        >
          <div className={flexRow} style={{ justifyContent: 'space-between' }}>
            <article className={flexColumn} style={{ gap: 4 }}>
              <p className={title.two}>ROLES, REGIONS & GROUPS</p>
              <p className={footnote.one} style={{ color: colors.neutral[60] }}>
                View the assigned group and region for each role to ensure
                proper access and alignment.
              </p>
            </article>
            {/* <AppUserAssignRoles /> */}

            <Button
              styleVariants={{
                kind: 'neutral',
                type: 'outlined',
                size: 'small'
              }}
              onClick={() => {
                redirect(ROUTE_NAMES.USER_ASSIGN_ROLES, options => ({
                  ...options,
                  queryParams: {
                    ...options.queryParams,
                    appId,
                    userId: user.id
                  }
                }))
              }}
            >
              Assign Roles
            </Button>
          </div>

          <Table columns={columns} data={userRoles} />
        </article>
      </article>

      <PreviewPermissionDrawer
        role={selectedRole}
        appId={appId}
        onClose={() => setSelectedRole(undefined)}
      />
    </>
  )
}

export default UserInfo
